import { Link } from "react-router-dom";

import Seo from "../../components/Seo";
import { seoFromPublicPage } from "../../seo/publicPages";
import ArticleLayout from "./ArticleLayout";
import RelatedGuides from "./RelatedGuides";

export default function GhidAnafPage() {
  return (
    <>
      <Seo {...seoFromPublicPage("/ghid-foaie-parcurs-anaf")} />
      <ArticleLayout
        eyebrow="Ghid complet"
        title="Foaia de parcurs și cerințele ANAF în 2026"
        lead="Ce trebuie să conțină o foaie de parcurs ca să fie acceptată la un control fiscal, de ce diferența dintre deducere 50% și 100% nu mai poate fi ignorată și ce formate folosesc astăzi firmele și PFA-urile."
      >
        <h2>De ce contează foaia de parcurs pentru firma ta</h2>
        <p>
          Foaia de parcurs este documentul prin care firma sau PFA-ul demonstrează că un
          vehicul a fost folosit <strong>exclusiv în scop economic</strong>. Fără acest
          document, Codul Fiscal limitează deducerea cheltuielilor și a TVA-ului la 50% —
          atât pentru combustibil, cât și pentru întreținere, asigurări, reparații sau
          piese de schimb.
        </p>
        <p>
          În contextul actual, cu prețuri la carburanți peste 8 lei/litru și costuri auto
          în creștere, diferența între 50% și 100% înseamnă bani reali — câteva sute sau
          mii de lei pe lună, pentru fiecare vehicul.
        </p>

        <h2>Cele 4 elemente obligatorii cerute de Codul Fiscal</h2>
        <p>
          Conform <strong>pct. 68 alin. (2)</strong> din Normele metodologice de aplicare
          a art. 298 din Codul Fiscal (TVA) și <strong>pct. 16 alin. (2)</strong> din
          normele aplicabile art. 25 (impozit pe profit), foaia de parcurs trebuie să
          cuprindă cel puțin:
        </p>
        <ol>
          <li>
            <strong>Categoria vehiculului</strong> — autoturism, autoutilitară, camion etc.
          </li>
          <li>
            <strong>Scopul și locul deplasării</strong> — concret și legat de activitatea
            economică (vizită client, livrare, bancă, vamă, ANAF).
          </li>
          <li>
            <strong>Kilometrii parcurși</strong> — la plecare și la sosire, cu totalul pe
            cursă.
          </li>
          <li>
            <strong>Norma proprie de consum</strong> de carburant pe kilometru parcurs.
          </li>
        </ol>
        <div className="article-doc__callout">
          <strong>Important</strong>
          <p>
            Nu există formular tipizat obligatoriu impus de ANAF. Firma poate folosi orice
            format — hârtie, Excel, PDF generat dintr-o aplicație — atât timp cât conține
            informațiile minime de mai sus și poate fi prezentat la cerere.
          </p>
        </div>

        <h2>Cine este obligat să întocmească foi de parcurs</h2>
        <p>
          Obligația apare pentru orice entitate juridică care deține sau folosește un
          vehicul cu masă totală sub 3.500 kg și sub 9 scaune și vrea să deducă integral
          cheltuielile:
        </p>
        <ul>
          <li>SRL-uri și SA-uri cu autoturisme în patrimoniu, leasing sau comodat</li>
          <li>PFA-uri și întreprinderi individuale care folosesc un autovehicul în activitate</li>
          <li>ONG-uri cu vehicule alocate proiectelor</li>
        </ul>
        <p>
          Pentru autoutilitarele peste 3,5 tone, vehiculele cu peste 9 scaune și
          categoriile speciale (servicii de urgență, taximetrie, școli de șoferi, agenți de
          vânzări), limitarea de 50% <strong>nu se aplică</strong> și foaia de parcurs nu
          este obligatorie pentru deducere. O autoutilitară sub 3.500 kg, cu cel mult 9
          locuri, rămâne în aceeași regulă ca autoturismul — detaliat în{" "}
          <Link to="/autoutilitara-vs-autoturism-deductibilitate">
            articolul despre autoutilitară și autoturism
          </Link>
          .
        </p>

        <h2>Deducere 50% vs 100% — diferența concretă</h2>
        <div className="article-doc__table-wrap">
          <table className="article-doc__table">
            <thead>
              <tr>
                <th>Regim</th>
                <th>Foaie de parcurs</th>
                <th>TVA dedus</th>
                <th>Cheltuială deductibilă</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Utilizare mixtă</td>
                <td>nu e necesară</td>
                <td>50%</td>
                <td>50%</td>
              </tr>
              <tr>
                <td>Utilizare exclusiv economică</td>
                <td><strong>obligatorie</strong></td>
                <td>100%</td>
                <td>100%</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Pentru un vehicul cu cheltuieli de 1.000 lei/lună (combustibil + întreținere) +
          190 lei TVA, regimul de 100% înseamnă o economie de aproximativ <strong>275 lei
          pe lună</strong> (95 lei TVA + 80 lei impozit pe profit + 100 lei cheltuială
          deductibilă suplimentară). Pe an, pentru 3 vehicule, vorbim de ~10.000 lei.
        </p>

        <h2>Format acceptat: hârtie, Excel sau aplicație online</h2>
        <p>
          Legislația acceptă orice format, atât timp cât foaia poate fi prezentată la
          cerere — pe hârtie, pe ecran sau printată. În practică, firmele moderne aleg
          una din trei variante:
        </p>

        <h3>Foaie consolidată lunară (PDF/Excel)</h3>
        <p>
          O singură foaie per vehicul per lună, cu toate rutele și alimentările într-un
          tabel. Reduce volumul de documente și permite reconcilierea la final de lună.
        </p>

        <h3>Excel cu FAZ încorporat</h3>
        <p>
          Pentru firme cu contabilitate internă riguroasă. Calculează automat consumul
          normat vs. real și generează fișa de activități zilnice.
        </p>

        <h3>Aplicație cloud</h3>
        <p>
          Rute zilnice introduse în aplicație, export lunar PDF (foaie + FAZ). Cea mai
          mică șansă de erori și completare retroactivă.
        </p>

        <h2>Lunar, săptămânal sau zilnic?</h2>
        <p>
          Legea nu impune o frecvență. O <strong>singură foaie pe lună</strong> cu toate
          deplasările și alimentările este perfect legală. În practică:
        </p>
        <ul>
          <li>
            Pentru firme cu trasee repetitive (același client, aceeași rută), o foaie pe
            lună este suficientă și eficientă.
          </li>
          <li>
            Pentru flote cu trasee variabile, completarea săptămânală scade riscul de
            uitare și de inconsistențe la control.
          </li>
          <li>
            Completarea retroactivă pe luni întregi este vizibilă pentru inspectori
            (aceeași cerneală, același scris) — riscul de respingere crește.
          </li>
        </ul>

        <h2>Greșeli frecvente care anulează deducerea la control</h2>

        <h3>Scop vag sau generic</h3>
        <p>
          „Deplasare serviciu" în loc de „livrare comandă #123 la SC Alpha SRL București".
          Inspectorii resping formulările care nu pot fi corelate cu activitatea firmei.
        </p>

        <h3>Kilometri nerealiști</h3>
        <p>
          Diferența între odometru și totalul foilor ridică suspiciuni. La control se
          compară km din bord cu cele declarate pe foi.
        </p>

        <h3>Consum inconsistent</h3>
        <p>
          Alimentările nu se potrivesc cu km parcurși și capacitatea rezervorului.
          Formula simplă: km × normă ≈ litri consumați.
        </p>

        <h3>Lipsa normei de consum</h3>
        <p>
          Norma proprie de consum este unul din cele 4 elemente obligatorii. Norma copiată
          din alt vehicul sau din cartea tehnică fără ajustare e vizibilă.
        </p>

        <h3>Foi completate retroactiv</h3>
        <p>
          Perioade lungi completate la grămadă, semnate odată. Inspectorii observă
          aceeași cerneală, același scris, același tipar pe 12 luni.
        </p>
        <div className="article-doc__callout article-doc__callout--warning">
          <strong>Atenție</strong>
          <p>
            ANAF poate refuza deducerea de 100% și recalcula impozit + TVA pentru toată
            perioada, plus penalități și dobânzi. La 3 vehicule cu vechime 2 ani, costul
            poate depăși 20.000 lei.
          </p>
        </div>

        <h2>Cât timp se păstrează foile de parcurs</h2>
        <p>
          Foile de parcurs sunt documente justificative contabile. Conform Legii
          contabilității nr. 82/1991 și OMFP 1969/2007, se păstrează <strong>minimum 5
          ani</strong> de la data întocmirii. Recomandarea practică: 10 ani, având în
          vedere prescripția generală fiscală.
        </p>

        <h2>Ce nu trebuie să faci</h2>

        <h3>Nu inventa trasee</h3>
        <p>
          Nu „construi" trasee la sfârșitul anului fără bază reală. ANAF compară foile cu
          facturile, bonurile și contractele clienților.
        </p>

        <h3>Nu copia același template</h3>
        <p>
          Un fișier identic pe 12 luni, completat manual cu aceeași mână, e vizibil la
          control. Variația naturală a traseelor trebuie să se reflecte în foi.
        </p>

        <h3>Nu omite scopul concret</h3>
        <p>
          „Scop personal" e o opțiune valabilă, dar atrage automat limitarea la 50%.
          Pentru 100%, fiecare cursă trebuie legată de activitatea economică.
        </p>

        <h2>Concluzia practică</h2>
        <p>
          Foaia de parcurs nu este un formular complicat. Cele 4 elemente obligatorii sunt
          clare. Dificultatea reală este <strong>menținerea ei la zi</strong>, lună de
          lună, pentru fiecare vehicul, cu coerența între kilometri, consum și rezervor.
          De aici vine valoarea unei aplicații dedicate: scoate povara de pe contabil sau
          administrator și asigură consistența cerută la control.
        </p>
        <RelatedGuides currentPath="/ghid-foaie-parcurs-anaf" />
      </ArticleLayout>
    </>
  );
}
